import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getAvailableAICapabilities } from '../config/aiCapabilities';
import { HomeSectionHeader } from '../components/home/HomeSectionHeader';
import { HOME_SURFACES } from '../components/home/homeSurfaces';
import { homeButtonClasses } from '../components/home/HomeButton';
import { MarketingPageCta } from '../components/marketing/MarketingPageCta';
import { MarketingPageHero } from '../components/marketing/MarketingPageHero';
import {
  marketingCardClass,
} from '../components/marketing/marketingSurfaces';
import { useSectionViewTracking } from '../lib/analytics';
import { usePageMetadata } from '../lib/seo';
import { cn } from '../lib/utils';

export function AICapabilitiesPage(): React.JSX.Element {
  const { t } = useTranslation(['ai', 'proof', 'common']);
  const capabilities = getAvailableAICapabilities();

  usePageMetadata({
    title: t('ai:meta.title'),
    description: t('ai:meta.description'),
    path: '/ia',
  });

  const traceabilityRef = useSectionViewTracking('ai_traceability');

  return (
    <div className="flex flex-col bg-brand-bg">
      <MarketingPageHero
        badge={t('ai:hero.badge')}
        title={t('ai:hero.title')}
        subtitle={t('ai:hero.subtitle')}
        actions={
          <>
            <Link
              to="/precios#demo-form"
              className={homeButtonClasses('accent', 'lg')}
            >
              {t('common:cta.requestDemo')}
            </Link>
            <a
              href="#ai-capabilities"
              className={homeButtonClasses('outline', 'lg')}
            >
              {t('ai:hero.viewCapabilities')}
            </a>
          </>
        }
      >
        <div className={cn(marketingCardClass, 'overflow-hidden p-4 md:p-6')}>
          <img
            src="/graphics/ai-decision-loop.svg"
            alt={t('ai:hero.title')}
            className="w-full"
          />
        </div>
      </MarketingPageHero>

      <section id="ai-capabilities" className={HOME_SURFACES.ivory}>
        <div className="section-padding !pt-0">
          <HomeSectionHeader
            badge={t('ai:capabilities.badge')}
            title={t('ai:capabilities.title')}
            subtitle={t('ai:capabilities.subtitle')}
            className="mb-12 md:mb-16"
          />

          <div className={cn(marketingCardClass, 'divide-y divide-brand-primary/10')}>
            {capabilities.map((capability) => (
              <article key={capability.code} className="p-8">
                <div className="grid grid-cols-1 gap-8 lg:grid-cols-[0.3fr_0.7fr]">
                  <div>
                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-[var(--ir-muted)]">
                      {t(capability.proofKey)}
                    </p>
                    <h3 className="mt-4 font-serif text-3xl font-bold text-brand-primary">
                      {t(capability.titleKey)}
                    </h3>
                  </div>
                  <div>
                    <p className="text-base leading-relaxed text-[var(--ir-muted)]">
                      {t(capability.descriptionKey)}
                    </p>
                    <p className="mt-6 text-sm font-semibold text-brand-teal">
                      {t(capability.impactKey)}
                    </p>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section ref={traceabilityRef} className={HOME_SURFACES.deep}>
        <div className="section-padding">
          <div className="grid grid-cols-1 items-start gap-12 lg:grid-cols-[0.95fr_1.05fr] lg:gap-16">
            <div>
              <HomeSectionHeader
                badge={t('ai:traceability.badge')}
                title={t('proof:ai.traceabilityTitle')}
                subtitle={t('proof:ai.traceabilityBody')}
                light
                className="mb-8"
              />
              <ul className="space-y-4 text-base leading-relaxed text-white/70">
                <li>• {t('ai:evidence.items.collections')}</li>
                <li>• {t('ai:evidence.items.voucher')}</li>
                <li>• {t('ai:evidence.items.pipeline')}</li>
              </ul>
            </div>

            <div className="overflow-hidden rounded-[1.5rem] border border-white/15 bg-white/5 p-6">
              <img
                src="/graphics/audit-hash-chain.svg"
                alt={t('proof:ai.traceabilityTitle')}
                className="w-full"
              />
            </div>
          </div>
        </div>
      </section>

      <section className={HOME_SURFACES.parchment}>
        <div className="section-padding">
          <div className={cn(marketingCardClass, 'p-8 md:p-12')}>
            <HomeSectionHeader
              badge={t('ai:evidence.badge')}
              title={t('ai:evidence.title')}
              subtitle={t('ai:evidence.subtitle')}
              className="mb-10"
            />
            <ul className="space-y-4 text-base leading-relaxed text-[var(--ir-muted)]">
              {['collections', 'voucher', 'pipeline'].map((item) => (
                <li key={item}>{t(`ai:evidence.items.${item}`)}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <MarketingPageCta
        badge={t('ai:cta.badge')}
        title={t('ai:cta.title')}
        subtitle={t('ai:cta.subtitle')}
        actions={
          <>
            <Link
              to="/precios#demo-form"
              className={homeButtonClasses('accent', 'lg')}
            >
              {t('common:cta.requestDemo')}
            </Link>
            <Link
              to="/modulos"
              className={cn(
                homeButtonClasses('outline', 'lg'),
                'border-white/30 bg-transparent text-white hover:bg-white/10'
              )}
            >
              {t('ai:cta.secondary')}
            </Link>
          </>
        }
      />
    </div>
  );
}
